/* Dynamic Plot code copyright 2015-2025 (c) Tuva Labs Inc. */
export const wrapDynPlotLabels = function(Dyn) {

    /***
     * utility class to position non-overlapping labels in the plot.
     */
    class PlotLabelsUtil {

        /**
         * Compute the position of the horizontal or vertical connector line from the label text
         * to a horizontal or vertical average line (e.g. mean), or region (e.g. St.Dev).
         * @param labelRect -- bbox of label text
         * @param widgetRect -- bbox of stat (horizontal/vertical line or spread region)
         * @param isXAxis -- if true, make a horizontal connector line to connect the label
         * @returns {Array} -- array of X,Y points to make line segments (e.g. 6 values = 3 points in line)
         */
        static getLineFromLabelToWidget( labelRect, widgetRect, isXAxis ) {
            // function zeroPlus( v ) {
            //     return Math.max( 0, v );
            // }
            const sp = Dyn.plotUtil.labelConnectorLineSpacer;
            let extraRect = new Dyn.mathUtil.Rect( widgetRect.x - sp, widgetRect.y - sp, widgetRect.width + 2 * sp, widgetRect.height + 2 * sp ),
                connector = [];

            if( isXAxis ) {
                // make a horizontal connector line
                if( widgetRect.top() > labelRect.bottom() ) {
                    if( extraRect.right() < labelRect.centerX() ) {
                        connector = [ // line to right then up to label
                            extraRect.right(), widgetRect.top(),
                            labelRect.centerX(), widgetRect.top(),
                            labelRect.centerX(), labelRect.bottom() ];
                    } else if( labelRect.centerX() < extraRect.left() ) {
                        connector = [ // line to left then up to label
                            extraRect.left(), widgetRect.top(),
                            labelRect.centerX(), widgetRect.top(),
                            labelRect.centerX(), labelRect.bottom() ];
                    }
                } else if( widgetRect.bottom() < labelRect.top() ) {
                    if( extraRect.right() < labelRect.centerX() ) {
                        connector = [ // line to right then down to label
                            extraRect.right(), widgetRect.bottom(),
                            labelRect.centerX(), widgetRect.bottom(),
                            labelRect.centerX(), labelRect.top() ];
                    } else if( labelRect.centerX() < extraRect.left() ) {
                        connector = [ // line to right then down to label
                            extraRect.left(), widgetRect.bottom(),
                            labelRect.centerX(), widgetRect.bottom(),
                            labelRect.centerX(), labelRect.top() ];
                    }
                } else if( extraRect.right() < labelRect.left() ) {
                    // line to right at center of label
                    let cy = Dyn.mathUtil.clipToMinMax( widgetRect.top(), widgetRect.bottom(), labelRect.centerY() );
                    connector = [ extraRect.right(), cy, labelRect.left(), cy ];
                } else if( labelRect.right() < extraRect.left() ) {
                    // line to left at center of label
                    let cy = Dyn.mathUtil.clipToMinMax( widgetRect.top(), widgetRect.bottom(), labelRect.centerY() );
                    connector = [ extraRect.left(), cy, labelRect.right(), cy ];
                }
                // else text overlaps line/region; hide the line
            } else {
                // make a vertical connector line
                if( widgetRect.right() < labelRect.left() ) {
                    if( labelRect.centerY() < extraRect.top() ) {
                        connector = [ // line up then right to label
                            widgetRect.right(), extraRect.top(),
                            widgetRect.right(), labelRect.centerY(),
                            labelRect.left(), labelRect.centerY() ];
                    } else if( extraRect.bottom() < labelRect.centerY() ) {
                        connector = [ // line down then right to label
                            widgetRect.right(), extraRect.bottom(),
                            widgetRect.right(), labelRect.centerY(),
                            labelRect.left(), labelRect.centerY() ];
                    }
                } else if( labelRect.right() < widgetRect.left() ) {
                    if( labelRect.centerY() < extraRect.top() ) {
                        connector = [ // line up then left to label
                            widgetRect.left(), extraRect.top(),
                            widgetRect.left(), labelRect.centerY(),
                            labelRect.right(), labelRect.centerY() ];
                    } else if( extraRect.bottom() < labelRect.centerY() ) {
                        connector = [ // line down then left to label
                            widgetRect.left(), extraRect.bottom(),
                            widgetRect.left(), labelRect.centerY(),
                            labelRect.right(), labelRect.centerY() ];
                    }
                } else if( labelRect.bottom() < extraRect.top() ) { // if text is above line/region
                    let cx = Dyn.mathUtil.clipToMinMax( widgetRect.left(), widgetRect.right(), labelRect.centerX() );
                    connector = [ cx, labelRect.bottom(), cx, extraRect.top() ];
                    //connector = new Dyn.mathUtil.Rect(  labelRect.centerX(), labelRect.bottom(), 0, zeroPlus( widgetRect.top()-labelRect.bottom()-sp ));
                } else if( labelRect.top() > extraRect.bottom() ) { // if text is below line/region
                    let cx = Dyn.mathUtil.clipToMinMax( widgetRect.left(), widgetRect.right(), labelRect.centerX() );
                    connector = [ cx, labelRect.top(), cx, extraRect.bottom() ];
                }
                // else text overlaps line/region; hide the line
            }

            return connector;
        }

        /**
         * Compute the position of the horizontal or vertical connector line from the label text
         * to a horizontal/vertical/diagonal line (the widget, e.g. least squares line).
         * All coordinates are in the Raphael.paper view coordinates
         * @param labelRect -- bbox of text
         * @param widgetRect -- bbox of horizontal/vertical/diagonal line
         * @param wantHorizontalLine -- if true, make a horizontal connector line to connect the label
         * @param viewPt1 -- 1st endpoint of visible line to connect to
         * @param viewPt2 -- 2nd endpoint
         * @returns @returns {Array|Rect} -- array of (x,y) line points or rect to make two points of line.
         */
        static getLineFromLabelToDiagonalLine( labelRect, widgetRect, wantHorizontalLine, viewPt1, viewPt2 ) {
            function zeroPlus( v ) {
                return Math.max( 0, v );
            }

            const sp = Dyn.plotUtil.labelConnectorLineSpacer;
            let connector = new Dyn.mathUtil.Rect( 0, 0, 0, 0 ), // default is hidden line (endpoints are the same)
                line = Dyn.plotUtil.findLineByTwoPoints( viewPt1.x, viewPt1.y, viewPt2.x, viewPt2.y ),
                widgetLineIsVertical = Dyn.mathUtil.isClose( viewPt1.x, viewPt2.x ),
                widgetLineIsHorizontal = Dyn.mathUtil.isClose( viewPt1.y, viewPt2.y ),
                x = labelRect.centerX(),
                y = labelRect.centerY(),
                labelAboveLine = (y < Math.min( viewPt1.y, viewPt2.y )),
                labelBelowLine = (y > Math.max( viewPt1.y, viewPt2.y )),
                labelToLeftOfLine = (x < Math.min( viewPt1.x, viewPt2.x )),
                labelToRightOfLine = (x > Math.max( viewPt1.x, viewPt2.x ));

            if( (labelAboveLine || labelBelowLine) && (labelToLeftOfLine || labelToRightOfLine) ) {
                // if label does not intersect diagonal line either horizontally or vertically, get 3-point line which ends at center of diagonal line
                let centerPtRect = new Dyn.mathUtil.Rect( widgetRect.centerX() - sp, widgetRect.centerY() - sp, 2 * sp, 2 * sp );
                connector = this.getLineFromLabelToWidget( labelRect, centerPtRect, wantHorizontalLine );
            } else {
                if( widgetLineIsHorizontal || labelAboveLine || labelBelowLine ) {
                    wantHorizontalLine = false; // connector line must be vertical to intersect diagonal line
                } else if( widgetLineIsHorizontal || labelToLeftOfLine || labelToRightOfLine ) {
                    wantHorizontalLine = true; // connector line must be horizontal to intersect diagonal line
                }
                if( wantHorizontalLine ) {
                    // make a horizontal connector line (connectorLineRect.height===0)
                    // note that we exclude a number of cases because generally vertical connector lines work better with horizontal labels
                    x = widgetLineIsVertical ? viewPt1.x :
                        ((y - line.intercept) * Dyn.mathUtil.invertSafely( line.slope )); // x,y is now intersection of widget line with horizontal line from center of label
                    if( labelRect.left() > x ) { // if text is to right of line/region
                        connector = new Dyn.mathUtil.Rect( x + sp, y, zeroPlus( labelRect.left() - x - sp ), 0 );
                    }
                    if( labelRect.right() < x ) { // if text is to left of line/region
                        connector = new Dyn.mathUtil.Rect( labelRect.right(), y, zeroPlus( x - labelRect.right() - sp ), 0 );
                    }
                    // else text overlaps line/region; hide the line
                } else {
                    // make a vertical connector line (connectorLineRect.width===0)
                    y = widgetLineIsHorizontal ? viewPt1.y :
                        (line.intercept + (x * line.slope)); // x,y is now intersection of widget line with vertical line from center of label
                    if( labelRect.bottom() < y ) { // if text is above line/region
                        connector = new Dyn.mathUtil.Rect( x, labelRect.bottom(), 0, zeroPlus( y - labelRect.bottom() - sp ) );
                    }
                    if( labelRect.top() > y ) { // if text is below line/region
                        connector = new Dyn.mathUtil.Rect( x, y + sp, 0, zeroPlus( labelRect.top() - y - sp ) );
                    }
                    // else text overlaps line/region; hide the line
                }
            }

            return connector;
        }

        /**
         * Compute the position of the horizontal or vertical connector line from the label text
         * to a horizontal/vertical/diagonal line (the widget, e.g. least squares line).
         * All coordinates are in the Raphael.paper view coordinates
         * @param labelRect -- bbox of text
         * @param connectPt -- point on the Fx line to connect to
         * @param rangeX -- range of valid X values for the function
         * @param wantHorizontalLine -- if true, make a horizontal connector line to connect the label
         * @returns @returns {Array|Rect} -- array of (x,y) line points or rect to make two points of line.
         */
        static getLineFromLabelToFxLine( labelRect, connectPt, rangeX, wantHorizontalLine ) {
            function zeroPlus( v ) {
                return Math.max( 0, v );
            }

            Dyn.assert( connectPt && rangeX );
            const sp = Dyn.plotUtil.labelConnectorLineSpacer;
            let connector = new Dyn.mathUtil.Rect( 0, 0, 0, 0 ), // default is hidden line (endpoints are the same)
                // line = Dyn.plotUtil.findLineByTwoPoints( viewPt1.x, viewPt1.y, viewPt2.x, viewPt2.y ),
                widgetLineIsVertical = false,
                widgetLineIsHorizontal = true,
                x = labelRect.centerX(),
                y = labelRect.centerY(),
                labelAboveLine = (y < connectPt.y),
                labelBelowLine = (y > connectPt.y),
                labelToLeftOfLine = (x < rangeX.min),
                labelToRightOfLine = (x > rangeX.max);

            if( (labelAboveLine || labelBelowLine) && (labelToLeftOfLine || labelToRightOfLine) ) {
                // if label does not intersect diagonal line either horizontally or vertically, get 3-point line which ends at center of diagonal line
                let centerPtRect = new Dyn.mathUtil.Rect( connectPt.x - sp, connectPt.y - sp, 2 * sp, 2 * sp );
                connector = this.getLineFromLabelToWidget( labelRect, centerPtRect, wantHorizontalLine );
            } else {
                if( widgetLineIsHorizontal || labelAboveLine || labelBelowLine ) {
                    wantHorizontalLine = false; // connector line must be vertical to intersect diagonal line
                } else if( widgetLineIsHorizontal || labelToLeftOfLine || labelToRightOfLine ) {
                    wantHorizontalLine = true; // connector line must be horizontal to intersect diagonal line
                }
                if( wantHorizontalLine ) {
                    // make a horizontal connector line (connectorLineRect.height===0)
                    // note that we exclude a number of cases because generally vertical connector lines work better with horizontal labels
                    // x = widgetLineIsVertical ? viewPt1.x :
                    //     ((y - line.intercept) * Dyn.mathUtil.invertSafely( line.slope )); // x,y is now intersection of widget line with horizontal line from center of label
                    // if( labelRect.left() > x ) { // if text is to right of line/region
                    //     connector = new Dyn.mathUtil.Rect( x + sp, y, zeroPlus( labelRect.left() - x - sp ), 0 );
                    // }
                    // if( labelRect.right() < x ) { // if text is to left of line/region
                    //     connector = new Dyn.mathUtil.Rect( labelRect.right(), y, zeroPlus( x - labelRect.right() - sp ), 0 );
                    // }
                    // else text overlaps line/region; hide the line
                } else {
                    // make a vertical connector line (connectorLineRect.width===0)
                    y = connectPt.y ; // x,y is now intersection of widget line with vertical line from center of label
                    if( labelAboveLine ) { // if text is above line/region
                        connector = new Dyn.mathUtil.Rect( x, labelRect.bottom(), 0, zeroPlus( y - labelRect.bottom() - sp ) );
                    }
                    if( labelBelowLine ) { // if text is below line/region
                        connector = new Dyn.mathUtil.Rect( x, y + sp, 0, zeroPlus( labelRect.top() - y - sp ) );
                    }
                    // else text overlaps line/region; hide the line
                }
            }

            return connector;
        }

        /**
         * Should the connector line (between label text and diagonal line), be horizontal or vertical?
         * Used by getLineFromLabelToDiagonalLine()
         * @param cellRect
         * @param widgetRect
         * @param viewPt1
         * @param viewPt2
         * @returns {boolean} -- true if line should be horizontal (parallel to x-axis)
         */
        static shouldConnectorLineBeHorizontal( cellRect, widgetRect, viewPt1, viewPt2 ) {
            // NOTE: this heuristic could be refined; compare view slope instead of width v height?
            return (widgetRect.width < widgetRect.height);
        }


        /**
         * Get the path string for a line connecting the text label to the widget.
         * The widget might be a horizontal line (so make a vertical connector line),
         *      a vertical line (so make a horizontal connector line),
         *      or a region or diagonal line.
         * If rect empty then path should be empty
         */
        static makeConnectorLinePathString( line ) {
            let path = '';
            if( line.top ) { // if non-empty Dyn.Rect, convert to array of points
                if( (line.width > 0) || (line.height > 0) ) {
                    line = [ line.left(), line.top(), line.right(), line.bottom() ];
                } else {
                    line = [];
                }
            }
            // array of points [x1,y1,x2,y2,...] to path of line
            if( line && line.length > 0 ) {
                line.forEach( ( coord, index ) =>
                    path += (Dyn.mathUtil.isEven( index ) ? ((index === 0) ? 'M' : 'L') : ',') + coord );
            } else {
                path = 'M0,0';
            }
            return path;
        }

        /**
         * Save the label position (with offset from widget to center of text label).
         * The offset is saved in normalized coordinates, so we can restore the relative
         * view position to a different sized plot. See restoreLabelPositionAndOffset()
         * @param textElement
         * @param labelRect
         * @param widgetRect
         */
        static saveLabelPositionAndOffset( textElement, labelRect, widgetRect ) {
            Dyn.assertValidRect( labelRect );
            Dyn.assertValidRect( widgetRect );
            let cellRect = textElement.data( "cellRect" );
            Dyn.assert( cellRect );
            let xCoordMap = new Dyn.mathUtil.CoordMap( 0, 1, cellRect.left(), cellRect.right() ),
                yCoordMap = new Dyn.mathUtil.CoordMap( 0, 1, cellRect.top(), cellRect.bottom() ),
                xView = labelRect.centerX() - widgetRect.right(),
                yView = labelRect.centerY() - widgetRect.top(),
                offset = { // save offset, note .a are view coords, .n are normalized coords, for restoreLabelPositionAndOffset()
                    x: new Dyn.axisUtil.NACoord( xCoordMap.transform( xView ), xView ),
                    y: new Dyn.axisUtil.NACoord( yCoordMap.transform( yView ), yView ),
                };
            textElement.data( "offset", offset ); // save in normalized coords for save/restore
            textElement.data( "labelRect", labelRect ); // save in view coords for comparison with other labels
        }

        /**
         * Return empty offset to indicate label has not yet been positioned,
         * or the offset from the widget in view coordinates.
         * See saveLabelPositionAndOffset()
         * @param textElement
         * @returns {{x,y}}
         */
        static restoreLabelPositionAndOffset( textElement ) {
            let //labelRect = textElement.data( "labelRect"),
                savedOffset = textElement.data( "offset" ),
                viewOffset = {}; // no offset, caller must position
            if( savedOffset ) {
                if( Number.isFinite( savedOffset.x.a ) ) {
                    // offset saved in this session, retrieve view coordinates
                    viewOffset.x = savedOffset.x.a;
                    viewOffset.y = savedOffset.y.a;
                } else {
                    // offset saved in a previous session, as normalized coordinates
                    // convert to view coordinates with the current cell size
                    let cellRect = textElement.data( "cellRect" );
                    Dyn.assert( cellRect );
                    let xCoordMap = new Dyn.mathUtil.CoordMap( 0, 1, cellRect.left(), cellRect.right() ),
                        yCoordMap = new Dyn.mathUtil.CoordMap( 0, 1, cellRect.top(), cellRect.bottom() );
                    viewOffset.x = xCoordMap.inverseTransform( savedOffset.x.n );
                    viewOffset.y = yCoordMap.inverseTransform( savedOffset.y.n );
                }
            }
            return viewOffset;
        }

        /**
         * Get the text bbox of all labels in the plot that have already been updated, so we avoid overlap with them.
         * If the 1st pass, ignore labels that have not yet been updated; they will reposition themselves later.
         * If the 2nd pass, get all rects, since all are up to date (but still ignore rect for thisTextElement)
         * @param plotElement
         * @param thisTextElement
         * @param isSecondPass
         * @returns {Array}
         */
        static getUpdatedLabelRectsInPlot( plotElement, thisTextElement, isSecondPass ) {
            let otherRects = [],
                continueScanning = true,
                plotShaper = plotElement.plotShaper,
                plotAdornments = plotShaper.adornments,
                thisCellIndex = thisTextElement.data( "cellIndex" );
            // for every plot element in priority order (array is sorted)
            for( let i = 0, j = plotShaper.plotElements.length; continueScanning && (i < j); i++ ) {
                let oneAdornmentArray = plotAdornments.getAdornmentsArray( plotShaper.plotElements[i].primaryType, false ),
                    numAdornments = oneAdornmentArray ? oneAdornmentArray.length : 0;
                // for every adornment in the plot element in priority order
                for( let m = 0; continueScanning && (m < numAdornments); m++ ) {
                    let otherAdornment = oneAdornmentArray[m],
                        otherTextEl = otherAdornment.getLabelTextEl && otherAdornment.getLabelTextEl(),
                        widgetRect = otherAdornment.getNonOverlappingWidgetRect && otherAdornment.getNonOverlappingWidgetRect(),
                        otherCellIndex = otherTextEl && otherTextEl.data( "cellIndex" ),
                        inSameCell = ((thisCellIndex >= 0) && (otherCellIndex >= 0)) ? thisCellIndex === otherCellIndex : true;
                    if( otherTextEl && inSameCell ) { // does the other adornment have a NonOverlappingLabel to avoid?
                        if( otherTextEl === thisTextElement ) {
                            Dyn.assert( plotShaper.plotElements[i] === plotElement );
                            // 1st pass: stop at this text element, 2nd pass: just skip this text element
                            continueScanning = Boolean( isSecondPass );
                        } else {
                            // found an element for which we should avoid overlap
                            if( otherTextEl.data( "labelRect" ) ) {
                                Dyn.assert( otherTextEl.data( "labelRect" ) );
                                otherRects.push( otherTextEl.data( "labelRect" ) );
                                Dyn.assertValidRect( otherRects[otherRects.length - 1] );
                            }
                        }
                    }
                    if( widgetRect && inSameCell ) { // does the other adornment have a NonOverlappingWidget rect to avoid?
                        otherRects.push( widgetRect ); // also avoid overlap with this rect
                        Dyn.assertValidRect( otherRects[otherRects.length - 1] );
                    }
                    //console.log("getUpdatedLabelRectsInPlot cells", thisCellIndex, otherCellIndex );
                }
            }
            //console.log( "getUpdatedLabelRectsInPlot", otherRects.length );
            return otherRects;
        }

        /**
         * Starting with our existing text label rectangle (bbox), find a bbox position that does not overlap
         * with other visible labels
         * @param textElement -- raphael paper.rect() element
         * @param labelRect --- Dyn.mathUtil.rect() element with current bbox of textElement
         * @param cellRect -- Dyn.mathUtil.rect() of plotting area for label (e.g. "Female" bin of plot by Gender)
         * @returns {Rect} -- new or modified text bbox (x or y adjusted)
         */
        static findNonOverlappingLabelRectInCell( plotElement, textElement, labelRect, cellRect, widgetRect, isSecondPass ) {

            function isNotOverLappingOtherRects( labelRect, otherRects ) {
                return (-1 === otherRects.findIndex( ( r ) => {
                    return labelRect.isOverlappingRect( r );
                } ));
            }

            // get the rectangles to compare
            Dyn.assertValidRect( labelRect );
            let otherRects = PlotLabelsUtil.getUpdatedLabelRectsInPlot( plotElement, textElement, isSecondPass ),
                spacer = Math.round( labelRect.height / 2 );

            // avoid text outside of cell bounds if possible
            let fitsCellWidth = (labelRect.width < cellRect.width), // enough space to show entire text in cell
                fitsCellHeight = (labelRect.height < cellRect.height); // enough space to show entire text in cell
            if( labelRect.right() > cellRect.right() ) {
                // X position: fix if the label right edge is outside of the cell
                let fitsRight = (labelRect.width < (cellRect.right() - widgetRect.right())), // enough space to fit text to right of average
                    fitsLeft = (labelRect.width < (widgetRect.left() - cellRect.left())); // enough space to fit text to left of average
                if( fitsRight ) {
                    labelRect.x = cellRect.right() - labelRect.width; // right-justify in cell
                } else if( fitsLeft ) {
                    labelRect.x = widgetRect.left() - labelRect.width - spacer; // position to left of average
                } else if( fitsCellWidth ) {
                    labelRect.x = cellRect.right() - labelRect.width - spacer; // position on left edge of cell, may overlap with average line or cell
                } else {
                    labelRect.x = cellRect.left() + spacer; // position on left edge of cell, may overlap with average line or cell
                }
            } else if( labelRect.centerX() < cellRect.left() ) {
                // X position: fix if more than have of label is to left of cell (may be completely invisible)
                labelRect.x = cellRect.left() + spacer; // position on left edge; OK if label width is longer than the cell width
            }
            if( labelRect.centerY() > cellRect.bottom() ) {
                // Y position: fix if more than have of label is to below the cell (may be completely invisible)
                labelRect.y = fitsCellHeight ? (cellRect.bottom() - labelRect.height) : (cellRect.centerY() - (labelRect.height / 2));
            } else if( labelRect.centerY() < cellRect.top() ) {
                // Y position: fix if more than have of label is to above the cell (may be completely invisible)
                labelRect.y = fitsCellHeight ? (cellRect.top()) : (cellRect.centerY() - (labelRect.height / 2));
            }

            // Y position: try different positions of labelRect in Y, within cell rect, until we find one or give up
            let textSlotsInY = Math.round( cellRect.height / labelRect.height ),
                notEnoughSlotsInY = (textSlotsInY < 2), // don't make non-overlapping in Y if cell height is too small
                nonOverlapping = notEnoughSlotsInY ? true : false; // repeat until true, or don't repeat at all if notEnoughSlotsInY

            for( let i = 0, j = textSlotsInY; (i < j && !nonOverlapping); i++ ) {
                if( isNotOverLappingOtherRects( labelRect, otherRects ) ) {
                    nonOverlapping = true;
                    break;
                }
                // if overlapping, move in Y and test again, but no more that (cell-height/text-height) times.
                let yTop = cellRect.top() + labelRect.height;
                if( (i === 0) && (labelRect.y < yTop) ) {  // try once only
                    labelRect.y -= labelRect.height;    // up one line
                    if( isNotOverLappingOtherRects( labelRect, otherRects ) ) {
                        nonOverlapping = true;
                        break;
                    }
                    labelRect.y += labelRect.height;    // undo up one line; does not fit
                }
                labelRect.y += labelRect.height; // down one line
                if( labelRect.bottom() > cellRect.bottom() ) {
                    // too low in Y, wrap around to the top
                    labelRect.y = yTop;
                }
            }
            // mark this element as a label, to avoid overlapping with this label in the future
            PlotLabelsUtil.saveLabelPositionAndOffset( textElement, labelRect, widgetRect );

            return labelRect;
        }

        /**
         * Get the initial or adjusted position of the text label, so that it does not overlap with other labels
         * positioned by PlotLabelsUtil.
         * Note: we preserve the "offset" with the textElement so the relative position to the average is more stable;
         *       if the text visible and does not overlap other labels, re-use the same offset from the average line/rect.
         * @param plotElement {Dyn.PlotElement} - object derived from Dyn.PlotElement that controls the widget
         * @param textElement - Raphael/SVG text object
         * @param isXAxis {boolean} - axis that label-to-widget connector line is parallel to
         * @param cell -- plot cell; holds view boundaries to position label within
         * @param widgetRect {Dyn.mathUtil.Rect} - bbox rectangle of plot widget we are labeling
         * @returns {Rect} - final label position
         */
        static setLabelPosition( plotElement, textElement, isXAxis, cell, widgetRect ) {
            var labelRect = PlotLabelsUtil.getLabelRectFromTextElement( textElement ),
                cellRect = new Dyn.mathUtil.Rect( cell ),
                cellIndex = cell.isPopulationCell ? -1 : plotElement.plotShaper.calcCellIndex( cell.xBinIndex || 0, cell.yBinIndex || 0 ),
                needOffset = Boolean( !textElement.data( "offset" ) ),
                offset = PlotLabelsUtil.restoreLabelPositionAndOffset( textElement ),  //textElement.data("offset") || {},
                anchor = {};

            // get the line anchor point, and the old or initial offset to from anchor to text center
            if( isXAxis ) {
                anchor.x = widgetRect.right();  // line or rect right
                anchor.y = widgetRect.top();
                if( needOffset ) {
                    let lineOffset = Dyn.plotUtil.labelConnectorLineSpacer,
                        lineLength = Math.round( labelRect.height / 2 );
                    offset.x = lineOffset + lineLength + (labelRect.width / 2);
                    offset.y = Math.min( 2 * labelRect.height, widgetRect.height / 2 ); // below top of line
                }
            } else {
                anchor.x = widgetRect.right();
                anchor.y = widgetRect.top(); // line or rect top
                if( needOffset ) {
                    let lineOffset = Dyn.plotUtil.labelConnectorLineSpacer,
                        lineLength = Math.round( labelRect.height / 2 );
                    if( labelRect )
                        offset.x = (labelRect.width > widgetRect.width) ?
                            -(widgetRect.width / 2) : // center in X if narrow widget (e.g. Std. Error or small bin)
                            -(labelRect.width / 2 + lineLength); // else right justify if wide cell+widget
                    offset.y = -(lineOffset + labelRect.height);  // center above line
                }
            }
            // center the text to anchor and offset
            labelRect.x = Math.round( anchor.x + offset.x - labelRect.width / 2 );
            labelRect.y = Math.round( anchor.y + offset.y - labelRect.height / 2 );

            // adjust the text, so it does not overlap with others, save results for auto-repositioning or manual adjustment (dragging)
            textElement.data( "isXAxis", isXAxis );
            textElement.data( "labelRect", labelRect );
            textElement.data( "widgetRect", widgetRect );
            textElement.data( "cellRect", cellRect );
            textElement.data( "cellIndex", cellIndex );
            return PlotLabelsUtil.findNonOverlappingLabelRectInCell( plotElement, textElement, labelRect, cellRect, widgetRect );
        }

        static getLabelRectFromTextElement( textElement ) {
            // add an extra 2 pixels around text for readability
            let labelRect = new Dyn.mathUtil.Rect( textElement.getBBox() );
            if( textElement.type === "foreignObject" ) {
                getMathQuillTextWidthHeight( labelRect );
            }
            labelRect.expand( Dyn.plotUtil.statLabelBorderDx, Dyn.plotUtil.statLabelBorderDy );
            return labelRect;

            function getMathQuillTextWidthHeight( rect ) {
                // get the correct height of the formula if a mathQuill object; other parameters are not useful
                let mqRootBlock = textElement.node.getElementsByClassName("mq-root-block")[0];
                if( mqRootBlock ) {
                    const rootRect = mqRootBlock.getBoundingClientRect();
                    // rootblock does not reflect width of elements, so we need to get the children
                    let min = 1e9, max = -1e9, childWidth = 0;
                    for (const child of mqRootBlock.children) {
                        const r = child.getBoundingClientRect();
                        const spacer = 10;
                        min = Math.min( min, r.left );
                        max = Math.max( max, r.right );
                        childWidth = max - min + spacer;
                    }
                    rect.height = rootRect.height;
                    rect.width = childWidth || rootRect.width;
                }
            }
        }

        static getTextRectFromLabelRect( labelRect ) {
            return new Dyn.mathUtil.Rect( labelRect ).expand( - Dyn.plotUtil.statLabelBorderDx, - Dyn.plotUtil.statLabelBorderDy );
        }

        static getHoverColorFromLabelColor( labelColor ) {
            // make a darker/lighter version of the label color for use when mouse hovering over the label
            Dyn.assert( /\#.*/.test( labelColor ) );
            let hsb = Dyn.colorUtil.svgColorString_to_PlatformColor( labelColor ),
                hoverBrightness = hsb.b * Dyn.plotUtil.statLabelHoverBrightness, // lighten or darken by some %
                hoverColor = Dyn.colorUtil.hsb_to_PlatformColor( hsb.h, hsb.s, hoverBrightness );
            return hoverColor.colorString;
        }

        static getAriaLabelForStatLabel( elementName ) {
            return Dyn.loc( "{{name}} label", { name: elementName } );
        }

        // update the lable and connector line position
        static updateLabelElements( set, labelRect, connectorLineRect, lineColor, clipRectString, animationTimeMS, animationEasing, isFillStyle=true ) {
            set.getLabelRectEl().animate( {
                x: labelRect.x,
                y: labelRect.y,
                width: labelRect.width,
                height: labelRect.height,
                fill: isFillStyle ? lineColor : Dyn.plotUtil.statLabelTextColor,
                stroke: isFillStyle ? "none" : lineColor,
                'clip-rect':clipRectString,
            }, animationTimeMS, animationEasing );
            set.getLabelTextEl().animate( {
                x: labelRect.centerX(),
                y: labelRect.centerY(),
                fill: Dyn.plotUtil.titleColor,
                "font-size":Dyn.preferences.getFontSize(),
                'clip-rect':clipRectString,
            }, animationTimeMS, animationEasing );
            set.getLabelConnectorEl().animate( {
                stroke: lineColor,
                path: this.makeConnectorLinePathString( connectorLineRect ),
                'clip-rect':clipRectString,
            }, animationTimeMS, animationEasing );
        }

        /** copy the label offsets that have an NACoord offset in X and Y
         * so we can save then restore the relative position to a different sized plot,
         * uses normalized coordinates (ox=0.2 is 2/10 of x-axis width, etc.)
         * The coordinates array can be sparse, not all labels have offsets */
        static copyLabelOffsets2D( labelOffsets ) {
            Dyn.assert( Array.isArray( labelOffsets ) || !labelOffsets );
            return labelOffsets?.map( coords => ( coords ? { ox: coords.ox, oy: coords.oy } : null )) || [];
        }
    }

    Dyn.PlotLabelsUtil = PlotLabelsUtil;

} // wrapDynPlotLabels
